import { useCallback } from 'react';
import { message } from 'antd';

import { sortAccountsByReadiness } from '../../utils/accountReadiness';

export const useAccountSelectionFlow = ({
  searchPublishAccounts,
  accountFilterForm,
  accountPagination,
  setAccountPagination,
  setAccountLoading,
  setAccountDataSource,
  setSelectedFilters,
  setCreateTaskDrawerVisible,
  setPublishAccountDrawerVisible,
  setPublishTimeDrawerVisible,
  setSelectedAccountKeys,
  setSelectedAccounts,
  selectedAccountMapRef,
  selectedAccountKeys,
  filterForm,
  selectedFilters,
  publishTimeForm,
  setScheduleParseResult,
}) => {
  const loadPublishAccounts = useCallback(async (params = {}) => {
    setAccountLoading(true);
    try {
      const page = params.page !== undefined ? params.page : accountPagination.current;
      const page_size = params.page_size !== undefined ? params.page_size : accountPagination.pageSize;

      const filterValues = accountFilterForm.getFieldsValue();
      const filters = {};
      if (filterValues.platforms && filterValues.platforms.length > 0) {
        filters.platforms = Array.isArray(filterValues.platforms) ? filterValues.platforms : [filterValues.platforms];
      }
      if (filterValues.status && filterValues.status.length > 0) {
        filters.status = Array.isArray(filterValues.status) ? filterValues.status : [filterValues.status];
      }

      const requestParams = {
        page,
        page_size,
        keyword: params.keyword || '',
        filters: Object.keys(filters).length > 0 ? filters : (params.filters || {}),
        sort: params.sort || { field: 'created_at', order: 'desc' },
      };

      const data = await searchPublishAccounts(requestParams);
      if (data.status === 'success') {
        setAccountDataSource(sortAccountsByReadiness(data.items || []));
        setAccountPagination({
          current: data.page,
          pageSize: data.page_size,
          total: data.total,
        });
      } else {
        message.error('获取账号列表失败');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('搜索发布账号失败:', error);
      message.error('搜索发布账号失败，请稍后重试');
    } finally {
      setAccountLoading(false);
    }
  }, [
    accountFilterForm,
    accountPagination.current,
    accountPagination.pageSize,
    searchPublishAccounts,
    setAccountDataSource,
    setAccountLoading,
    setAccountPagination,
  ]);

  const handleFilterFormFinish = useCallback((values) => {
    setSelectedFilters(values);
    setCreateTaskDrawerVisible(false);
    setPublishAccountDrawerVisible(true);
    setSelectedAccountKeys([]);
    setSelectedAccounts([]);
    selectedAccountMapRef.current.clear();
    accountFilterForm.resetFields();
    loadPublishAccounts();
  }, [
    accountFilterForm,
    loadPublishAccounts,
    selectedAccountMapRef,
    setCreateTaskDrawerVisible,
    setPublishAccountDrawerVisible,
    setSelectedAccountKeys,
    setSelectedAccounts,
    setSelectedFilters,
  ]);

  const handleAccountFilter = useCallback(() => {
    setAccountPagination((prev) => ({
      ...prev,
      current: 1,
    }));
    loadPublishAccounts({ page: 1 });
  }, [loadPublishAccounts, setAccountPagination]);

  const handleBackToFilterSelection = useCallback(() => {
    setPublishAccountDrawerVisible(false);
    setCreateTaskDrawerVisible(true);
    if (selectedFilters) {
      filterForm.setFieldsValue(selectedFilters);
    }
  }, [filterForm, selectedFilters, setCreateTaskDrawerVisible, setPublishAccountDrawerVisible]);

  const handleConfirmAccountSelection = useCallback(() => {
    if (selectedAccountKeys.length === 0) {
      message.warning('请至少选择一个发布账号');
      return;
    }
    const selected = selectedAccountKeys
      .map((accountId) => selectedAccountMapRef.current.get(accountId))
      .filter(Boolean);
    setSelectedAccounts(selected);
    setPublishAccountDrawerVisible(false);
    setPublishTimeDrawerVisible(true);
    publishTimeForm.resetFields();
    setScheduleParseResult(null);
  }, [
    publishTimeForm,
    selectedAccountKeys,
    selectedAccountMapRef,
    setPublishAccountDrawerVisible,
    setPublishTimeDrawerVisible,
    setScheduleParseResult,
    setSelectedAccounts,
  ]);

  const handleBackToAccountSelection = useCallback(() => {
    setPublishTimeDrawerVisible(false);
    setPublishAccountDrawerVisible(true);
    publishTimeForm.resetFields();
    setScheduleParseResult(null);
  }, [
    publishTimeForm,
    setPublishAccountDrawerVisible,
    setPublishTimeDrawerVisible,
    setScheduleParseResult,
  ]);

  return {
    loadPublishAccounts,
    handleFilterFormFinish,
    handleAccountFilter,
    handleBackToFilterSelection,
    handleConfirmAccountSelection,
    handleBackToAccountSelection,
  };
};
